import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";
import {
  quandoLegivel,
  ROTULO_DA_ENTREGA,
  ROTULO_DO_EVENTO_DE_WEBHOOK,
  resumirEntrega,
  useAlterarWebhook,
  useCriarWebhook,
  useEntregasDoWebhook,
  useExcluirWebhook,
  useGirarSegredoDoWebhook,
  useOrganizacoesParaAutomacao,
  useReenviarEntrega,
  useWebhooks,
  validarUrlDoWebhook,
  type Webhook,
} from "../../nucleo/automacao";
import { mensagemDoConsole } from "../../nucleo/console";
import { CaixaDeMarcar, CampoDeEscolha } from "./apoio";
import { SegredoMostradoUmaVez } from "./SegredoMostradoUmaVez";

function EntregasDoWebhook({ webhook }: { webhook: Webhook }) {
  const entregas = useEntregasDoWebhook(webhook.id);
  const reenviar = useReenviarEntrega(webhook.id);
  return (
    <Cartao titulo={`Entregas de ${webhook.nome}`} className="mt-3">
      {entregas.isPending ? <p role="status">Carregando entregas…</p> : null}
      {entregas.isError ? <Aviso tom="erro">{mensagemDoConsole(entregas.error)}</Aviso> : null}
      {reenviar.isError ? <Aviso tom="erro">{mensagemDoConsole(reenviar.error)}</Aviso> : null}
      {entregas.data && entregas.data.length === 0 ? <p className="nota-rodape">Nenhuma entrega ainda.</p> : null}
      <ul data-testid="lista-de-entregas">
        {entregas.data?.map((entrega) => (
          <li key={entrega.id} data-testid={`entrega-${entrega.estado}`}>
            <Chip estado={entrega.estado === "entregue" ? "ok" : entrega.estado === "falhou" ? "erro" : "aguardando"}>
              {ROTULO_DA_ENTREGA[entrega.estado]}
            </Chip>{" "}
            {ROTULO_DO_EVENTO_DE_WEBHOOK[entrega.evento] ?? entrega.evento} · {resumirEntrega(entrega)}
            {entrega.estado === "pendente" ? ` Próxima tentativa: ${quandoLegivel(entrega.proxima_tentativa_em)}.` : ""}
            {entrega.estado === "falhou" ? (
              <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={() => reenviar.mutate(entrega.id)}>
                Reenviar
              </Botao>
            ) : null}
            <ul>
              {entrega.tentativas_feitas.map((t) => (
                <li key={t.numero} className="nota-rodape">
                  {`Tentativa ${t.numero}: ${t.status_http ?? "sem resposta"}${t.erro ? ` (${t.erro})` : ""} em ${t.duracao_ms} ms`}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

const EVENTOS = Object.entries(ROTULO_DO_EVENTO_DE_WEBHOOK);

export function PainelDeWebhooks() {
  const webhooks = useWebhooks();
  const organizacoes = useOrganizacoesParaAutomacao();
  const criar = useCriarWebhook();
  const alterar = useAlterarWebhook();
  const girar = useGirarSegredoDoWebhook();
  const excluir = useExcluirWebhook();
  const [nome, setNome] = useState("");
  const [url, setUrl] = useState("");
  const [eventos, setEventos] = useState<string[]>(["ticket.aberto"]);
  // Vazio = ainda não escolhido: o escopo é obrigatório, sem padrão que dê a um webhook "todos os clientes".
  const [escopo, setEscopo] = useState("");
  const [problema, setProblema] = useState<string | null>(null);
  const [segredo, setSegredo] = useState<{ titulo: string; valor: string } | null>(null);
  const [vendo, setVendo] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const nomesDasOrganizacoes = useMemo(
    () => Object.fromEntries((organizacoes.data ?? []).map((o) => [o.id, o.nome_fantasia ?? o.razao_social])),
    [organizacoes.data],
  );

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const erro =
      nome.trim().length < 2
        ? "Dê um nome ao destino."
        : validarUrlDoWebhook(url) ??
          (eventos.length === 0 ? "Escolha ao menos um evento." : escopo === "" ? "Escolha o escopo do webhook." : null);
    setProblema(erro);
    if (erro) return;
    criar.mutate(
      escopo === "todas"
        ? { nome: nome.trim(), url: url.trim(), eventos, escopo: "todas" }
        : { nome: nome.trim(), url: url.trim(), eventos, escopo: "organizacao", organizacao_id: escopo },
      {
        onSuccess: (criado) => {
          setSegredo({ titulo: `Segredo do webhook ${criado.nome}`, valor: criado.segredo });
          setNome("");
          setUrl("");
          setEventos(["ticket.aberto"]);
          setEscopo("");
        },
      },
    );
  }

  const falha = criar.error ?? alterar.error ?? girar.error ?? excluir.error ?? webhooks.error;

  return (
    <div>
      {segredo ? (
        <SegredoMostradoUmaVez
          titulo={segredo.titulo}
          valor={segredo.valor}
          rotuloDoBotao="Copiar segredo"
          aoFechar={() => setSegredo(null)}
          data-testid="segredo-do-webhook"
        />
      ) : null}
      <Cartao titulo="Novo webhook" className="mb-4">
        <p className="nota-rodape mb-3">
          Cada evento escolhido chega ao endereço abaixo como JSON assinado com o segredo do webhook.
        </p>
        <form onSubmit={submeter} data-testid="formulario-de-webhook" noValidate>
          <Campo rotulo="Nome do destino" value={nome} onChange={(e) => setNome(e.target.value)} />
          <Campo rotulo="Endereço do destino (URL)" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://" />
          <p className="campo__rotulo">Eventos</p>
          {EVENTOS.map(([valor, rotulo]) => (
            <CaixaDeMarcar
              key={valor}
              rotulo={`${rotulo} (${valor})`}
              marcada={eventos.includes(valor)}
              aoMudar={(marcada) => setEventos((atual) => (marcada ? [...atual, valor] : atual.filter((e) => e !== valor)))}
            />
          ))}
          <CampoDeEscolha
            rotulo="Escopo do webhook"
            valor={escopo}
            aoMudar={setEscopo}
            vazio="Escolha…"
            opcoes={[
              { valor: "todas", rotulo: "Todas as organizações" },
              ...(organizacoes.data ?? []).map((o) => ({ valor: o.id, rotulo: o.nome_fantasia ?? o.razao_social })),
            ]}
          />
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {falha ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(falha)}</Aviso> : null}
          <Botao type="submit" disabled={criar.isPending}>{criar.isPending ? "Criando…" : "Criar webhook"}</Botao>
        </form>
      </Cartao>

      <section aria-labelledby="titulo-webhooks">
        <h2 className="titulo-secao" id="titulo-webhooks">Webhooks</h2>
        {webhooks.isPending ? <p role="status">Carregando webhooks…</p> : null}
        {webhooks.data && webhooks.data.length === 0 ? <Aviso tom="marca">Nenhum webhook cadastrado.</Aviso> : null}
        <div className="lista-de-itens" data-testid="lista-de-webhooks">
          {webhooks.data?.map((webhook) => (
            <Cartao
              key={webhook.id}
              titulo={webhook.nome}
              data-testid={`webhook-${webhook.nome}`}
              acessorio={<Chip estado={webhook.ativo ? "ok" : "neutro"}>{webhook.ativo ? "Ativo" : "Desativado"}</Chip>}
            >
              <p className="texto-preservado">{webhook.url}</p>
              <p className="nota-rodape mt-1">
                {webhook.eventos.map((e) => ROTULO_DO_EVENTO_DE_WEBHOOK[e] ?? e).join(", ")}
              </p>
              <p className="nota-rodape mt-1">
                {webhook.organizacao_id
                  ? `Só os chamados de ${nomesDasOrganizacoes[webhook.organizacao_id] ?? "uma organização"}`
                  : "Chamados de todas as organizações"}
              </p>
              <div className="flex gap-2 mt-2 flex-wrap">
                <Botao type="button" variante="fantasma" aria-label={`Ver entregas de ${webhook.nome}`} onClick={() => setVendo(vendo === webhook.id ? null : webhook.id)}>
                  {vendo === webhook.id ? "Ocultar entregas" : "Ver entregas"}
                </Botao>
                <Botao type="button" variante="fantasma" aria-label={`${webhook.ativo ? "Desativar" : "Ativar"} webhook ${webhook.nome}`} onClick={() => alterar.mutate({ id: webhook.id, corpo: { ativo: !webhook.ativo } })}>
                  {webhook.ativo ? "Desativar" : "Ativar"}
                </Botao>
                <Botao
                  type="button"
                  variante="fantasma"
                  aria-label={`Girar segredo de ${webhook.nome}`}
                  onClick={() => girar.mutate(webhook.id, { onSuccess: (r) => setSegredo({ titulo: `Novo segredo do webhook ${webhook.nome}`, valor: r.segredo }) })}
                >
                  Girar segredo
                </Botao>
                {confirmando === webhook.id ? (
                  <Botao type="button" aria-label={`Confirmar exclusão do webhook ${webhook.nome}`} onClick={() => excluir.mutate(webhook.id, { onSettled: () => setConfirmando(null) })}>
                    Confirmar exclusão
                  </Botao>
                ) : (
                  <Botao type="button" variante="fantasma" aria-label={`Excluir webhook ${webhook.nome}`} onClick={() => setConfirmando(webhook.id)}>Excluir</Botao>
                )}
              </div>
              {vendo === webhook.id ? <EntregasDoWebhook webhook={webhook} /> : null}
            </Cartao>
          ))}
        </div>
      </section>
    </div>
  );
}
